import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { encerrarSessao, lerToken } from '../services/sessao';
import './SaasPortal.css';

function isSaasSystemRole(role?: string): boolean {
  const normalized = String(role || '').toLowerCase();
  return normalized === 'super_admin'
    || normalized === 'super_admin_assistente'
    || normalized === 'saas_admin'
    || normalized === 'saas_admin_assistente';
}

function decodeTokenPayload(token: string): any | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export default function SaasLoginPage() {
  const navigate = useNavigate();
  const { login, isCarregando } = useAuth();

  const [emailLocal, setEmailLocal] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');

  // ⚠️ SEGURANÇA CRÍTICA: Apenas emails @gestaocreche.com.br podem acessar o SaaS
  const DOMINIO_SAAS_OBRIGATORIO = '@gestaocreche.com.br';
  const emailDigitado = emailLocal.trim().toLowerCase();
  const emailCompleto = emailDigitado.includes('@')
    ? emailDigitado
    : `${emailDigitado}${DOMINIO_SAAS_OBRIGATORIO}`;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setErro('');

    // ⚠️ Validação de Segurança: rejeita qualquer email que não seja do domínio correto
    if (!emailLocal.trim()) {
      setErro('E-mail é obrigatório.');
      return;
    }

    if (!emailCompleto.toLowerCase().endsWith(DOMINIO_SAAS_OBRIGATORIO)) {
      setErro(`❌ Acesso negado. Apenas emails ${DOMINIO_SAAS_OBRIGATORIO} podem acessar o SaaS.`);
      return;
    }

    try {
      await login(emailCompleto, senha);

      const token = lerToken('saas') || '';
      const payload = decodeTokenPayload(token);
      const roles = Array.isArray(payload?.roles) ? payload.roles : [];
      const tipoUsuario = String(payload?.tipo_usuario || '').toLowerCase();
      const autorizadoSaas = roles.some((role: string) => isSaasSystemRole(role)) || isSaasSystemRole(tipoUsuario);

      if (!autorizadoSaas) {
        encerrarSessao('saas');
        setErro('Acesso negado. Este login é exclusivo para administração SaaS. Usuários de creche devem usar o login padrão do sistema.');
        return;
      }

      // O AuthContext já atualiza o estado. Redirecionamos para o dashboard SaaS.
      navigate('/saas/dashboard');
    } catch (err: any) {
      setErro(err.response?.data?.message || 'Falha na autenticação do portal SaaS.');
    }
  }

  return (
    <div className="saas-login-page">
      <div className="saas-login-bg">
        <div className="saas-login-orb saas-login-orb-1" />
        <div className="saas-login-orb saas-login-orb-2" />
        <div className="saas-login-grid-bg" />
      </div>

      <div className="saas-login-container">
        <div className="saas-login-card">
          <header className="saas-login-header">
            <div className="saas-logo-icon">💠</div>
            <h1>Portal <strong>SaaS</strong></h1>
            <p>Acesso exclusivo — Alencastro Consultoria</p>
          </header>

          <form className="saas-login-form" onSubmit={handleLogin}>
            {erro && <div className="saas-login-error">⚠️ {erro}</div>}

            <div className="saas-login-field">
              <label htmlFor="emailLocal">E-mail administrativo</label>
              <div className="saas-input-wrapper">
                <span className="saas-input-icon">✉️</span>
                <input
                  id="emailLocal"
                  type="text"
                  placeholder="seu_email"
                  value={emailLocal}
                  onChange={(e) => setEmailLocal(e.target.value)}
                  required
                />
                <span className="saas-input-domain">{DOMINIO_SAAS_OBRIGATORIO}</span>
              </div>
              <small style={{ color: '#0066cc', marginTop: '4px', display: 'block' }}>
                Email completo: <strong>{emailCompleto}</strong>
              </small>
            </div>

            <div className="saas-login-field">
              <label htmlFor="senha">Senha de segurança</label>
              <div className="saas-input-wrapper">
                <span className="saas-input-icon">🔒</span>
                <input
                  id="senha"
                  type="password"
                  placeholder="••••••••"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="saas-login-btn" disabled={isCarregando}>
              {isCarregando ? (
                <>
                  <span className="saas-spinner-small" />
                  Autenticando...
                </>
              ) : (
                'Acessar Sistema SaaS'
              )}
            </button>
          </form>

          <div className="saas-register-footer">
            <p>
              Não possui uma conta?{' '}
              <Link to="/saas/register" className="saas-link-footer">
                Registre-se aqui
              </Link>
            </p>
          </div>

          <footer className="saas-login-footer">
            <p>© {new Date().getFullYear()} GestãoCreche SaaS</p>
            <p>Infraestrutura de alta disponibilidade</p>
          </footer>
        </div>

        <div className="saas-login-info">
          <div className="saas-info-item">
            <h3>🛡️ Segurança de Elite</h3>
            <p>Monitoramento Proativo 24/7 e Logs de Auditoria Imutáveis.</p>
          </div>
          <div className="saas-info-item">
            <h3>🚀 Escalabilidade Global</h3>
            <p>Arquitetura Multi-Tenant com isolamento total de dados.</p>
          </div>
          <div className="saas-info-item">
            <h3>⚡ Performance Crítica</h3>
            <p>Baixa latência e alta consistência para gestão educacional.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
